/**
 * The Market pages' glossary: every term the mock underlines on a Market page, with its card copy
 * verbatim from the mock bundle (`index-BCnJtz61.js`: the `Xp` table-head map, Overview's stat
 * terms, Floorsheet's title). Static copy by design: no endpoint serves definitions.
 *
 * Two words have two cards, because the mock writes them twice. A table head's "Turnover" and
 * "Transactions" explain one row's figure; the Overview stat strip's explain the whole session's.
 * Their keys say which: `turnover` / `turnoverToday`, `transactions` / `transactionsToday`.
 *
 * Read by TermTip (`term="price"`); a caller with a one-off card passes `title` and `body` there
 * instead of growing this file, or spreads one of `GLOSSARY_TEMPLATES` (copy with a figure in it).
 */
export interface GlossaryEntry {
  /** The card's 12/600 first line. Not always the label on screen: "Price (Rs)" opens "Price (LTP)". */
  title: string
  /** One or two plain sentences, 12/400 in the secondary ink. */
  body: string
}

const PRICE: GlossaryEntry = {
  title: 'Price (LTP)',
  body: 'The last traded price: the price of the most recent trade. Traders call it LTP.',
}

const CHANGE: GlossaryEntry = {
  title: 'Change',
  body: 'How much the price moved since the last trading day’s close.',
}

export const MARKET_GLOSSARY = {
  /** Table heads "Price (Rs)" and "LTP (Rs)": Movers, Gainers & Losers, 52-Week, Stock Prices. */
  price: PRICE,
  /** Table heads "Change" and "% chg": Movers, Gainers & Losers, Stock Prices. */
  change: CHANGE,
  /** Table head "Volume (kitta)": Movers' Gainers, Losers and Volume tabs. */
  volume: {
    title: 'Volume',
    body: 'How many shares changed hands. Nepal counts shares in kitta: one kitta is one share.',
  },
  kitta: {
    title: 'Kitta',
    body: 'Nepal counts shares in kitta. One kitta is one share.',
  },
  /** Table head "Turnover": Movers' Turnover tab. */
  turnover: {
    title: 'Turnover',
    body: 'The total money that changed hands: price times shares, added up for every trade.',
  },
  /** Table head "Transactions": Movers' Transactions tab. */
  transactions: {
    title: 'Transactions',
    body: 'How many separate trades happened. One trade can be many kitta.',
  },
  open: { title: 'Open', body: 'The price of the first trade of the day.' },
  high: { title: 'High', body: 'The highest price anyone paid during the day.' },
  low: { title: 'Low', body: 'The lowest price anyone paid during the day.' },
  close: {
    title: 'Close',
    body: 'The price at the end of the day. Tomorrow’s change is measured from it.',
  },
  /** Table head "52W high": 52-Week (both tabs). */
  high52: { title: '52-week high', body: 'The highest price in the past year.' },
  /** Table head "52W low": 52-Week (both tabs). */
  low52: { title: '52-week low', body: 'The lowest price in the past year.' },
  /** Movers' Brokers tab: "Bought (kitta)". */
  bought: { title: 'Bought', body: 'Kitta this broker’s clients bought.' },
  /** Movers' Brokers tab: "Sold (kitta)". */
  sold: { title: 'Sold', body: 'Kitta this broker’s clients sold.' },
  /** Movers' Brokers tab: "Net kitta". */
  netKitta: {
    title: 'Net kitta',
    body: 'Kitta bought minus kitta sold. Above zero means more buying than selling.',
  },
  marketCap: {
    title: 'Market cap',
    body: 'The price times every share. What the market says the whole company is worth.',
  },
  /** Floorsheet's trade table: "Rate (Rs)". */
  rate: { title: 'Rate', body: 'The price per share in this one trade.' },
  /** Floorsheet's head line ("Today across NEPSE"). */
  floorsheet: {
    title: 'Floorsheet',
    body: 'Every finished trade on NEPSE for the day: the two brokers, the kitta, the rate and the time. Numbers are broker codes.',
  },
  /** Overview's stat strip: "Turnover". */
  turnoverToday: {
    title: 'Turnover',
    body: 'The total rupee value of every share that changed hands today.',
  },
  /** Overview's stat strip: "Shares traded". */
  sharesTraded: {
    title: 'Shares traded',
    body: 'How many shares (kitta) changed hands, whatever their price.',
  },
  /** Overview's stat strip: "Transactions". */
  transactionsToday: {
    title: 'Transactions',
    body: 'How many separate trades were matched on the exchange.',
  },
  /** Overview's stat strip: "Total market cap". */
  totalMarketCap: {
    title: 'Total market cap',
    body: 'What every listed company is worth together at today’s prices.',
  },
  // br:kit — Broker Analysis heads (COMPARE-bt "Missing glossary terms", COMPARE-bl §0.3).
  /**
   * Large holdings' "Average buy price": the broker's buy turnover over the kitta it bought. The
   * mock reuses its portfolio "Average cost" card ("…with fees included"), which is not true of a
   * broker's rate, so this is written for the figure it sits on, in the mock's voice.
   */
  averageBuyPrice: {
    title: 'Average buy price',
    body: 'What this broker’s clients paid for each share on average: the money spent divided by the kitta bought. Fees are not included.',
  },
  /** Large holdings' "Share of paid-up" (verbatim from the mock). */
  shareOfPaidUp: {
    title: 'Share of paid-up',
    body: 'How much of all the company’s shares this is.',
  },
  /**
   * A "Bought" column in rupees: Sector flow's sectors, a broker's own money columns. The mock puts
   * the kitta card (`bought`) on Sector flow's money column, which says the wrong thing about it;
   * this is the same card for a value, worded to hold for a sector row and a broker row alike.
   */
  boughtValue: {
    title: 'Bought',
    body: 'The money clients spent buying these shares, in rupees.',
  },
  /** A "Sold" column in rupees: the money counterpart of `sold`, as `boughtValue`. */
  soldValue: {
    title: 'Sold',
    body: 'The money clients got from selling these shares, in rupees.',
  },

  // sz:kit — Company page (COMPARE-cp1/cp2/cp3) and Public Issues (COMPARE-pi) heads and labels,
  // verbatim from the mock bundle (`index-CljiLnKi.js`). Signal Zone (sz1, sz2) needs only `price`.
  // Copy that names a figure, a symbol or the company's kind is in `GLOSSARY_TEMPLATES` below.

  // Stock Overview's key numbers (cp1 "Glossary terms to add"). Their sector-median line ("Bank
  // median 19.0.") and EPS's "Up 10.9% on the year." are figures: pass them as TermTip's `note`.
  range52: {
    title: '52-week range',
    body: 'The lowest and highest prices of the past year. The dot is the last close.',
  },
  eps: {
    title: 'EPS',
    body: 'Earnings per share. The year’s profit divided by the number of shares.',
  },
  pe: {
    title: 'P/E',
    body: 'Price to earnings. How many rupees you pay for one rupee of yearly profit.',
  },
  roe: {
    title: 'Return on equity',
    body: 'Profit as a share of the owners’ money. Higher means the capital works harder.',
  },
  // Stock Compare's factor rows (cp1).
  cmpPe: {
    title: 'Price vs profit',
    body: 'Rupees paid for one rupee of yearly profit. Lower is better.',
  },
  cmpPb: {
    title: 'Price vs net worth',
    body: 'Price against what the books say it owns. Lower is better.',
  },
  cmpEps: {
    title: 'Profit per share',
    body: 'Yearly profit divided by every share. Higher is better.',
  },
  cmpBook: {
    title: 'Net worth per share',
    body: 'What one share owns on paper. Higher is better.',
  },
  dividendYield: {
    title: 'Dividend yield',
    body: 'Last year’s payout against today’s price. Higher is better.',
  },
  cmpRoe: {
    title: 'Return on equity',
    body: 'Profit earned on the owners’ money. Higher is better.',
  },
  cmpNpl: {
    title: 'Loans not repaid on time',
    body: 'Share of loans going bad. Lower is safer. Lower is better.',
  },
  // Stock Overview's momentum rows (cp1): the mock opens these in the row, not as a tip.
  rsi: {
    title: 'RSI',
    body: 'Relative strength, 0 to 100. Above 70 is often called stretched, below 30 washed out.',
  },
  stochastic: {
    title: 'Stochastic',
    body: 'Where the close sits in the recent high–low range. Low numbers mean near the bottom of it.',
  },
  moneyFlowIndex: {
    title: 'Money flow index',
    body: 'RSI weighted by volume. Around 50 means buying and selling money are close.',
  },
  adx: {
    title: 'ADX',
    body: 'Trend strength, not direction. Above 25 usually means a clear trend.',
  },
  macd: {
    title: 'MACD',
    body: 'The gap between two moving averages. Below its signal line means recent prices are falling faster.',
  },

  // Stock Advanced tabs' card titles (cp2 "Glossary terms to add"). Bank measures, so "the bank".
  npl: {
    title: 'Non-performing loans (NPL)',
    body: 'A loan is non-performing once it is 90 days late. The bank sets money aside for it.',
  },
  provisionCoverage: {
    title: 'Provision coverage',
    body: 'Money set aside against bad loans, as a share of them. 100% means every bad loan is covered.',
  },
  loanClassification: {
    title: 'Loan classification',
    body: 'NRB grades loans by how late they are. Watchlist loans are the next likely to go bad.',
  },
  car: {
    title: 'Capital adequacy (CAR)',
    body: 'Capital absorbs losses before depositors do. Below the NRB minimum, the bank must stop growing.',
  },
  cdRatio: {
    title: 'Credit to deposit (CD)',
    body: 'Loans as a share of deposits. The rest stays liquid for withdrawals.',
  },
  interestSpread: {
    title: 'Interest spread',
    body: 'What the bank charges borrowers minus what it pays savers, in percentage points.',
  },
  casa: {
    title: 'Deposit mix (CASA)',
    body: 'Current and savings accounts: deposits that pay little or no interest.',
  },
  /** Value tab's ROE card (cp2): a longer card than Overview's `roe`. */
  roeValue: {
    title: 'Return on equity (ROE)',
    body: 'Profit after tax as a share of shareholders’ equity. The profit rate on the owners’ own money.',
  },
  nim: {
    title: 'Net interest margin (NIM)',
    body: 'Interest earned minus interest paid, as a share of the assets that earn it.',
  },
  costOfFunds: {
    title: 'Cost of funds',
    body: 'The average interest the bank pays on its deposits and borrowings.',
  },
  costToIncome: {
    title: 'Cost to income ratio',
    body: 'Operating cost as a share of operating income. Lower is leaner.',
  },
  /** Trading tab. The mock writes "sma" in lower case; kept. */
  trendSma: {
    title: 'Trend (sma 20, 50, 200)',
    body: 'A simple moving average (sma) is the average close over the last N days. Price below all three reads as a downtrend.',
  },
  supportResistance: {
    title: 'Support and resistance',
    body: 'Support is a price where buyers stepped in before; resistance is where sellers did.',
  },
  rsiMomentum: {
    title: 'Momentum (RSI 14)',
    body: 'The relative strength index weighs recent rises against falls over 14 days, on a 0 to 100 scale.',
  },
  liquidity: {
    title: 'Liquidity (30 days)',
    body: 'The average value traded each day. More is easier to buy or sell without moving the price.',
  },

  // Stock Financials essentials and income heads, Events (cp3 §14).
  /** Financials' essentials strip: a different card from Overview's (`GLOSSARY_TEMPLATES.bookValue`). */
  bookValueFinancials: {
    title: 'Book value',
    body: 'What the owners’ money works out to for each share.',
  },
  returnOnAssets: {
    title: 'Return on assets',
    body: 'Profit as a share of everything the bank owns.',
  },
  baseRate: {
    title: 'Base rate',
    body: 'The lowest rate the bank may lend at. Loans are priced above it.',
  },
  operatingMargin: { title: 'Operating margin', body: 'Operating profit as a share of income.' },
  netMargin: { title: 'Net margin', body: 'Profit after tax as a share of income.' },
  debtToEquity: {
    title: 'Debt to equity',
    body: 'What the bank owes for each rupee of the owners’ money. Banks run high because deposits count as debt.',
  },
  /** Financials' income table "Change" head: a year-on-year change, not the day's (`change`). */
  financialChange: {
    title: 'Change',
    body: 'Against the same quarter a year earlier. Quarterly filings are year to date, so Q4 covers the whole year.',
  },
  /**
   * Financials' income table "Sector median" head. The mock's card says "The average commercial
   * bank"; owner ruling C9 puts the sector MEDIAN everywhere, so it says median.
   */
  sectorMedian: {
    title: 'Sector median',
    body: 'The median commercial bank for the same quarter.',
  },
  bookClosure: {
    title: 'Book closure',
    body: 'The day the company freezes its shareholder list. Shares settled in your demat by then earn the dividend, bonus or right shares on offer.',
  },

  // Public Issues list heads (COMPARE-pi "Glossary: terms the list needs"). For the band card.
  issueUnits: { title: 'Units', body: 'How many shares are on offer in this issue.' },
  issuePrice: { title: 'Price', body: 'What one share costs in this issue.' },
  /** The card under a head that keeps the wire's "Issue price" label (the mock's long form). */
  issuePriceAtPar: {
    title: 'Issue price',
    body: 'What you pay for each share when you apply. At par means the share’s face value, usually Rs 100, with nothing added.',
  },
  issueOpens: { title: 'Opens', body: 'The first day you can apply.' },
  issueCloses: { title: 'Closes', body: 'The last day you can apply.' },
  prospectus: {
    title: 'Prospectus',
    body: 'The official document about the company and this issue. It also lists the risks.',
  },
  priceRange: {
    title: 'Price range',
    body: 'Big investors bid inside this range. The final price comes from their bids.',
  },
  notice: { title: 'Notice', body: 'The official auction notice, with the rules and the dates.' },
  ratio: {
    title: 'Ratio',
    body: 'New shares you may buy for the shares you hold. 1:1 means one new share for each share you own.',
  },
  minBidPrice: { title: 'Minimum bid price', body: 'The lowest price per share you may bid.' },
  minBidQty: { title: 'Minimum bid quantity', body: 'The fewest shares you may bid for.' },

  // rest:kit — Portfolio heads (COMPARE-pf1 "Head terms": Overview's Avg cost, Price, Market value,
  // P/L; Holdings' Price, Market value, P/L %), verbatim from the mock bundle (`index-CSfSyRuL.js`,
  // `dx`), with the rest of its portfolio set (Invested, Day P/L, Total P/L, Weight) for the pf2
  // pages. "Price (Rs)" on a holdings table is the `price` card above.
  /** "Avg cost" / "Average cost": the member's own cost per share, fees in. */
  avgCost: {
    title: 'Average cost',
    body: 'What you paid for each share on average, with fees included. Your profit is counted from here.',
  },
  invested: { title: 'Invested', body: 'The money you put in: shares times your average cost.' },
  marketValue: { title: 'Market value', body: 'What your shares are worth now at today’s price.' },
  dayPl: {
    title: 'Day P/L',
    body: 'Profit or loss today: how much the value moved since the last close.',
  },
  totalPl: {
    title: 'Total P/L',
    body: 'Profit or loss on shares you still hold: market value minus what you invested.',
  },
  pl: {
    title: 'P/L',
    body: 'Profit or loss on shares you still hold: market value minus what you invested.',
  },
  plPercent: { title: 'P/L %', body: 'Your profit or loss as a share of what you invested.' },
  weight: { title: 'Weight', body: 'How much of your whole portfolio this holding is.' },
} as const satisfies Record<string, GlossaryEntry>

export type GlossaryTerm = keyof typeof MARKET_GLOSSARY

/**
 * sz:kit — the mock's cards whose copy carries a figure, a symbol or the company's kind, written
 * for NABIL (a bank) in the mock. Each returns a `GlossaryEntry`, so it spreads straight into
 * TermTip: `<TermTip {...GLOSSARY_TEMPLATES.tradeSummary('NABIL')}>Trade summary</TermTip>`.
 * With the mock's own arguments each returns its copy verbatim.
 */
export const GLOSSARY_TEMPLATES = {
  /** Overview's "P/B" (cp1). `kind`: "bank" in the mock; "company" for a stock that is not one. */
  pb: (kind = 'bank'): GlossaryEntry => ({
    title: 'P/B',
    body: `Price to book. The price against what the ${kind} owns, less what it owes, per share.`,
  }),
  /** Overview's "Book value" (cp1). `kind` as `pb`. */
  bookValue: (kind = 'bank'): GlossaryEntry => ({
    title: 'Book value',
    body: `What each share would get if the ${kind} sold everything and paid every debt.`,
  }),
  /** Floorsheet's "Trade summary" (cp1). */
  tradeSummary: (symbol: string): GlossaryEntry => ({
    title: 'Trade summary',
    body: `Every trade in ${symbol} on the chosen day, added up. Average trade size is the kitta traded divided by the number of trades.`,
  }),
  /** SWOT's rank legend (cp2): "Rank among 19 banks". `peers`: the plural, "banks". */
  rankInSector: (count: number, peers = 'banks'): GlossaryEntry => ({
    title: `Rank among ${String(count)} ${peers}`,
    body: 'Best on the left. The grey tick is the sector median.',
  }),
  /**
   * Peers' "Sector" head (cp2), with `period` "Q4 2082–83". The mock says "The mean of"; owner
   * ruling C9 makes it the median. "In bold blue" is the mock's best-figure style: change it with
   * the table if the page marks the best figure another way.
   */
  peersSector: (count: number, period: string, peers = 'banks'): GlossaryEntry => ({
    title: 'Sector',
    body: `The median of the ${String(count)} ${peers} that filed ${period}. The best company on a measure is in bold blue.`,
  }),
  /**
   * Returns' "Growth of 100" (cp2). `since`: "a year ago". `dividends`: whether the company lines
   * add back cash dividends. `fixedDeposit`: whether the chart draws the FD line (no API serves
   * the rate yet, so leave it off until one does).
   */
  growthOf100: ({
    since,
    dividends,
    fixedDeposit = false,
  }: {
    since: string
    dividends: boolean
    fixedDeposit?: boolean
  }): GlossaryEntry => ({
    title: 'Growth of 100',
    body: [
      `Every line starts at 100 ${since}, so you can compare them.`,
      dividends
        ? 'Company lines add back cash dividends; indices are price only.'
        : 'Indices are price only.',
      fixedDeposit ? 'Fixed deposit is the average 1-year FD rate, compounded.' : null,
    ]
      .filter((sentence) => sentence !== null)
      .join(' '),
  }),
  /** Trading's "Relative performance" (cp2). `index`: "banking sub-index". */
  relativePerformance: (symbol: string, index = 'banking sub-index'): GlossaryEntry => ({
    title: 'Relative performance',
    body: `${symbol}’s price change against the ${index} over the same three months.`,
  }),
} as const
